import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import type { BoxSessionFormikEntity } from "@/modules/boxSlotSession/entities/boxSessionFormikEntity"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { cn } from "@/modules/lib/utils"
import type {
  ProgramInfiniteSessionFormikType,
  ProgramOnOffSessionFormikType,
  ProgramSessionInputKey,
} from "@/modules/sessions/programSessionFormikType"
import { MediaExistingBlockImage } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocImage"
import { MediaExistingBlockPdf } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocPdf"
import { MediaExistingBlockVideo } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocVideo"
import { MediaExistingBlockVimeo } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocVimeo"
import { MediaExistingBlockYoutube } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocYoutube"
import type { MediaBlockFormikType } from "@/modules/sessions/sessionForm/blocks/mediaBlock/mediaBlockFormikTypes"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { type SessionBlockMediasFormikType } from "@/modules/sessions/sessionForm/sessionBlockFormikTypes"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import type { SimpleFormik } from "@/typings/formik"
import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { EditIcon } from "@images/svgIcons/editIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { word } from "@modules/core/utils/i18n"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { isVimeoLink, isYoutubeLink } from "@modules/utils/mediaUtils"

type Props = {
  formik:
    | SimpleFormik<BoxSessionFormikEntity, ProgramSessionInputKey>
    | SimpleFormik<ProgramInfiniteSessionFormikType, ProgramSessionInputKey>
    | SimpleFormik<ProgramOnOffSessionFormikType, ProgramSessionInputKey>
  block: SessionBlockMediasFormikType
  blockIndex: number
  moveBlock: (blockIndex: number, direction: "UP" | "DOWN") => Promise<void>
  deleteBlock: (blockIndex: number) => Promise<void>
  className?: string
} & React.ComponentProps<"div">

export function MediaExistingBloc({ formik, block, moveBlock, deleteBlock, blockIndex, className, ...props }: Props) {
  const { theme } = useTheme()
  const { navigateToMediaForm } = useSessionSidebar()
  const { hideSidebarStackOnTop } = useSidebar()

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-3xl border border-borderDefault p-3",
        theme === Theme.OwnerDark && "bg-neutral-50",
        className,
      )}
      {...props}
    >
      {/* Media card header */}
      <Text fontStyle="TS" className="text-textDefault-body">
        {word("sessionForm.blocks.media")}
      </Text>

      {/* Media content */}
      <div className="flex flex-col gap-4">
        {block.content.medias.map((media, index) =>
          media.type === MediaType.S3_VIDEO ? (
            <MediaExistingBlockVideo key={index} media={media} />
          ) : media.type === MediaType.S3_IMAGE ? (
            <MediaExistingBlockImage key={index} media={media} />
          ) : media.type === MediaType.S3_PDF ? (
            <MediaExistingBlockPdf key={index} media={media} />
          ) : media.type === MediaType.YOUTUBE && media.url && isYoutubeLink(media.url) ? (
            <MediaExistingBlockYoutube key={index} media={media} />
          ) : media.type === MediaType.VIMEO && media.url && isVimeoLink(media.url) ? (
            <MediaExistingBlockVimeo key={index} media={media} id={media.id ?? index.toString()} />
          ) : (
            <Text fontStyle="BM" className="wrap-break-word" key={index}>
              {word("sessionForm.blocks.add_media.missing_error")}
            </Text>
          ),
        )}
      </div>

      {/* Media card footer*/}
      <div className="flex flex-row items-center justify-end gap-1">
        <EllipsisMenu propsContent={{ side: "bottom" }}>
          {blockIndex !== 0 && formik.values.blocks.length >= 1 && (
            <DropdownMenuItem key="moveUp" onClick={() => void moveBlock(blockIndex, "UP")}>
              <ArrowIcon
                arrowDirection="top"
                color={blockIndex === 0 ? "var(--neutral-300)" : "var(--neutral-900)"}
                width={16}
                height={16}
              />
              {word("sessionForm.blocks.move.up")}
            </DropdownMenuItem>
          )}
          {blockIndex < formik.values.blocks.length - 1 && formik.values.blocks.length >= 1 && (
            <DropdownMenuItem key="moveDown" onClick={() => void moveBlock(blockIndex, "DOWN")}>
              <ArrowIcon
                arrowDirection="bottom"
                color={blockIndex === formik.values.blocks.length - 1 ? "var(--neutral-300)" : "var(--neutral-900)"}
                width={16}
                height={16}
              />
              {word("sessionForm.blocks.move.down")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            key="deleteBlock"
            onClick={() => {
              void deleteBlock(blockIndex)
            }}
          >
            <TrashIcon color="var(--neutral-900)" width={20} height={20} />
            {word("sessionForm.exercise.remove_exo.button")}
          </DropdownMenuItem>
        </EllipsisMenu>

        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="primary"
                size="iconMd"
                onClick={() =>
                  navigateToMediaForm(block.content, blockIndex, async (mediaBlock: MediaBlockFormikType) => {
                    await formik.setFieldValue(
                      "blocks",
                      formik.values.blocks.map((block, index) =>
                        index === blockIndex ? { ...block, content: mediaBlock } : block,
                      ),
                    )
                    hideSidebarStackOnTop()
                  })
                }
              >
                <EditIcon color="currentColor" width={16} height={16} className="text-textDefault-heading" />
              </Button>
            }
          />
          <TooltipContent side="bottom">
            <Text fontStyle="LM" className="text-textDefault-body">
              {word("global.edit")}
            </Text>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}
